import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Input } from '../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  GraduationCap,
  Loader2,
  Clock,
  Shield,
  Upload,
  Eye,
  MoreHorizontal,
  FileText,
  Calendar,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Filter,
  Search,
  Book,
  Award,
  Link as LinkIcon,
  Edit2,
  Download,
  CheckCircle,
  Wand2,
  Trash2,
  Mail
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { formatBackendDate } from '../../lib/dateUtils';
import TrainingDetailDrawer from './TrainingDetailDrawer';
import InlineDocumentViewer from '../shared/InlineDocumentViewer';
import TrainingCertificateExtractor from './TrainingCertificateExtractor';
import TrainingIntakeWizard from './TrainingIntakeWizard';
import TrainingRequestDialog from './TrainingRequestDialog';

import EvidenceReviewViewerDialog from '../compliance/EvidenceReviewViewerDialog';
import DaysRemainingBadge from '../compliance/DaysRemainingBadge';
import API_BASE from '../../utils/apiBase';
import {
  getTrainingLibraryBannerState,
  getVisibleCanonicalQualifications,
  partitionCanonicalQualifications,
} from './trainingLibraryBanner';
import { getTrainingRenewalRequestDefaults } from './trainingRenewalRequestDefaults';
import { verifiedSafeguardingCoverage, verifiedSafeguardingNeedsScopeReview, safeguardingReviewedScope, isSafeguardingScopeReviewForEmployee } from './safeguardingCoverage';
import { requireVerifiedTrainingReview } from './trainingReviewOutcome';
import { getTrainingReviewQueue, getTrainingFollowUpQueue, getTrainingOpenItems, getCertificateEvidenceRows, getSourceReviewQueue, summarizeTrainingReviewQueue } from './trainingReviewNavigation';

const API = API_BASE;

// Status styling for training items
const STATUS_STYLES = {
  current: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: CheckCircle2, label: 'Current' },
  completed: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: Clock, label: 'Submitted, not reviewed' },
  verified: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: CheckCircle2, label: 'Verified' },
  expiring_soon: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: Clock, label: 'Due soon' },
  due_soon: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: Clock, label: 'Due soon' },
  expired: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: AlertTriangle, label: 'Expired' },
  overdue: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: AlertTriangle, label: 'Overdue' },
  missing: { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200', icon: XCircle, label: 'Missing' },
  pending: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: Clock, label: 'Awaiting admin review' },
  awaiting_review: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', icon: Clock, label: 'Awaiting admin review' },
  partial: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: AlertTriangle, label: 'Partial' },
  rejected: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', icon: XCircle, label: 'Rejected / action required' },
  proposed: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', icon: Wand2, label: 'Awaiting admin review' },
  needs_information: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: AlertTriangle, label: 'Needs information' },
  action_required: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: AlertTriangle, label: 'Action required' },
  conditional: { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200', icon: Clock, label: 'Conditional' },
  cannot_assess: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: AlertTriangle, label: 'Cannot assess' },
};

const TIMING_CURRENT_STATUSES = new Set(['current', 'verified', 'valid', 'no_expiry']);
const TIMING_DUE_SOON_STATUSES = new Set(['expiring_soon', 'due_soon']);
const TIMING_EXPIRED_STATUSES = new Set(['expired', 'overdue']);
const PENDING_REVIEW_STATUSES = new Set(['completed', 'pending', 'awaiting_review', 'proposed']);
const REJECTED_STATUSES = new Set(['rejected', 'action_required']);
const LIVE_PROPOSAL_STATUSES = new Set([
  'proposed',
  'pending',
  'pending_review',
  'awaiting_review',
  'needs_clarification',
]);

const requiresReverification = (item) => Boolean(
  item?.source_evidence_removed ||
  item?.needs_review ||
  item?.needs_review_reason === 'source_certificate_deleted' ||
  item?.needs_review_reason === 'certificate_evidence_removed_by_admin' ||
  item?.needs_review_reason === 'evidence_replaced_reverification_required'
);
const isTrainingVerified = (item) => {
  if (requiresReverification(item)) return false;
  return Boolean(item?.verified || item?.is_verified || item?.status === 'verified');
};
const getTrainingTimingStatus = (item) => (
  item?.status_band ||
  item?.renewal_status ||
  item?.timing_status ||
  item?.renewalStatus ||
  item?.status
);
const getTrainingExpiryDate = (item) => item?.expires_at || item?.expiry_date;
const isExpiryDatePast = (expiryDate) => {
  if (!expiryDate) return false;
  const parsed = new Date(expiryDate);
  if (Number.isNaN(parsed.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  parsed.setHours(0, 0, 0, 0);
  return parsed < today;
};
const isTrainingExpiredOrOverdue = (item) => (
  TIMING_EXPIRED_STATUSES.has(getTrainingTimingStatus(item)) ||
  isExpiryDatePast(getTrainingExpiryDate(item))
);
const isTrainingDueSoon = (item) => (
  !isTrainingExpiredOrOverdue(item) &&
  TIMING_DUE_SOON_STATUSES.has(getTrainingTimingStatus(item))
);
const isTrainingCurrent = (item) => (
  !isTrainingExpiredOrOverdue(item) &&
  (TIMING_CURRENT_STATUSES.has(getTrainingTimingStatus(item)) || isTrainingDueSoon(item))
);
const isMandatoryTrainingSatisfied = (item) => isTrainingVerified(item) && isTrainingCurrent(item);

const isEvidenceOnFile = (item) => Boolean(
  item?.completed_at ||
  item?.source_document_id ||
  item?.certificate_url ||
  item?.evidence_files?.length ||
  item?.evidence?.length
);

const normaliseCanonicalTrainingRecord = (record) => {
  const expiryDate = record?.expiry_date || record?.expires_at;
  const needsReverification = requiresReverification(record);
  const verified = !needsReverification && Boolean(record?.verified || record?.is_verified || record?.verification_status === 'verified');
  const timingStatus = isExpiryDatePast(expiryDate) ? 'expired' : 'valid';
  const status = record?.verification_status === 'rejected'
    ? 'rejected'
    : needsReverification
      ? 'completed'
    : verified
      ? (timingStatus === 'expired' ? 'expired' : 'verified')
      : (record?.status || 'completed');

  return {
    ...record,
    id: record?.id,
    record_id: record?.id,
    code: record?.requirement_id || record?.mapped_training_code || record?.id,
    title: record?.training_name || record?.mapped_training_title || 'Training record',
    status,
    completed_at: record?.completion_date || record?.completed_at,
    expires_at: expiryDate,
    is_verified: verified,
    verified,
    is_required: Boolean(record?.mandatory || record?.is_mandatory),
    is_mandatory: Boolean(record?.mandatory || record?.is_mandatory),
    provider: record?.provider_name || record?.provider,
    source_document_id: record?.source_document_id || record?.certificate_document_id,
  };
};

/**
 * AuditReadyTrainingMatrix - Comprehensive training management for CQC audit readiness
 * 
 * Features:
 * 1. Mandatory Training section - 6 required baseline items with blocker logic
 * 2. Training Library - All qualifications including additional/optional
 * 3. Certificates section - Certificate-centric view with extraction details
 * 4. Summary cards showing complete picture
 */
export default function AuditReadyTrainingMatrix({
  employeeId,
  employeeName,
  role,
  onUploadCertificate,
  onViewCertificate,
  onRefresh
}) {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [sourceErrors, setSourceErrors] = useState({
    matrix: false,
    certificates: false,
    proposedItems: false,
    trainingRecords: false,
    capabilityProfiles: false,
  });
  const [activeTab, setActiveTab] = useState('mandatory');
  const [reviewSourceDocumentId, setReviewSourceDocumentId] = useState(null);
  
  // Data states
  const [mandatoryTraining, setMandatoryTraining] = useState([]);
  const [canonicalTrainingRecords, setCanonicalTrainingRecords] = useState([]);
  const [showHistoricalQualifications, setShowHistoricalQualifications] = useState(false);
  const [certificates, setCertificates] = useState([]);
  const [proposedItems, setProposedItems] = useState([]);
  const [capabilityReadiness, setCapabilityReadiness] = useState(null);
      const [unmappedItems, setUnmappedItems] = useState([]);
    const [dependencyWarnings, setDependencyWarnings] = useState([]);
  const [summary, setSummary] = useState({
    totalRequired: 0,
    current: 0,
    needsRenewal: 0,
    missing: 0,
    blockers: 0,
    additionalQualifications: 0,
    certificatesUploaded: 0,
    needsReview: 0
  });
  
  // UI states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [extractorOpen, setExtractorOpen] = useState(false); // AI Certificate Extractor dialog
  const [extractExistingDocumentId, setExtractExistingDocumentId] = useState(null);
  const [certificatePreview, setCertificatePreview] = useState(null);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [requestDialogDefaults, setRequestDialogDefaults] = useState({
    trainingTypes: [],
    includeRenewals: false,
  });
  const [capabilityMutating, setCapabilityMutating] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [trainingReviewOpen, setTrainingReviewOpen] = useState(false);
  const [trainingReviewItem, setTrainingReviewItem] = useState(null);
  const [trainingReviewPurpose, setTrainingReviewPurpose] = useState('proposed');
  
  // Delete training state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteLinkedRecords, setDeleteLinkedRecords] = useState(false);
  const [unverifyDialogOpen, setUnverifyDialogOpen] = useState(false);
  const [unverifyItem, setUnverifyItem] = useState(null);
  const [unverifyReason, setUnverifyReason] = useState('');
  const [unverifying, setUnverifying] = useState(false);

  // Remove certificate state
  const [removeCertDialogCert, setRemoveCertDialogCert] = useState(null);
  const [noExpiryChecked, setNoExpiryChecked] = useState(false);
  const [optionalApproveConfirmItem, setOptionalApproveConfirmItem] = useState(null);
  const [removingCert, setRemovingCert] = useState(false);

  // Approve-and-verify state
  const [quickVerifying, setQuickVerifying] = useState({}); // itemId → bool
  const [selectedForBatch, setSelectedForBatch] = useState(new Set());
  const [batchVerifying, setBatchVerifying] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const refreshTimersRef = useRef([]);
  const hasLoadedTrainingRef = useRef(false);
  
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'super_admin';

  const closeTrainingReview = useCallback(() => {
    setTrainingReviewOpen(false);
    setTrainingReviewItem(null);
    setTrainingReviewPurpose('proposed');
  }, []);

  // Fetch all training data
  const fetchTrainingData = useCallback(async ({ silent = false } = {}) => {
    if (!employeeId) return;
    
    if (silent && hasLoadedTrainingRef.current) {
      setIsRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      if (!silent) setLoadError(false);
      setSourceErrors({ matrix: false, certificates: false, proposedItems: false, trainingRecords: false, capabilityProfiles: false });
      // Fetch training matrix data
      const [matrixResult, proposedResult, docsResult, capabilityResult] = await Promise.allSettled([
        axios.get(`${API}/employees/${employeeId}/training/matrix`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/employees/${employeeId}/training/proposed-items`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/employees/${employeeId}/training/certificates`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/employees/${employeeId}/training/capability-readiness`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (matrixResult.status !== 'fulfilled') {
        setSourceErrors({
          matrix: true,
          certificates: docsResult.status !== 'fulfilled',
          proposedItems: proposedResult.status !== 'fulfilled',
          trainingRecords: false,
          capabilityProfiles: capabilityResult.status !== 'fulfilled',
        });
        throw new Error('Training matrix unavailable');
      }
      
      // Process matrix data
      const matrixData = matrixResult.value?.data || {};

      // ✅ NEW BACKEND STRUCTURE
      const requiredItems = matrixData.role_required_requirements || [];
      // Historical temporary_internal rows are internal training evidence only.
      // Keep them in backend audit history, but never present them as Care
      // Certificate achievements or real qualifications.
      const allQualifications = getVisibleCanonicalQualifications(
        matrixData.all_qualifications || []
      );
      const matrixDependencyWarnings = matrixData.dependency_warnings || [];
      setDependencyWarnings(matrixDependencyWarnings);

      setUnmappedItems(matrixData.unmapped_items || []);
      const completionSummary = matrixData.completion_summary || {};

      // ✅ SET STATE CORRECTLY
      setMandatoryTraining(requiredItems);
      setCanonicalTrainingRecords(allQualifications);

      // Handle proposed items - ensure it's an array
      const proposedFailed = proposedResult.status !== 'fulfilled';
      const proposedData = proposedFailed ? null : proposedResult.value?.data;
      const proposedArrayRaw = Array.isArray(proposedData) ? proposedData :
                               proposedData?.items || proposedData?.proposed_items || [];
      const proposedArray = proposedArrayRaw.filter((item) =>
        LIVE_PROPOSAL_STATUSES.has(String(item?.status || '').toLowerCase())
      );
      setProposedItems(proposedArray);

      // Get training certificates from merged endpoint (canonical + legacy)
      const certificatesFailed = docsResult.status !== 'fulfilled';
      const docsData = certificatesFailed ? null : docsResult.value?.data;
      const trainingCerts = docsData?.certificates || [];
      setCertificates(trainingCerts);

      const capabilityFailed = capabilityResult.status !== 'fulfilled';
      setCapabilityReadiness(capabilityFailed ? null : capabilityResult.value?.data || null);

      setSourceErrors({
        matrix: false,
        certificates: certificatesFailed,
        proposedItems: proposedFailed,
        trainingRecords: false,
        capabilityProfiles: capabilityFailed,
      });
      
      // Use completion_summary only
      const apiSummary = matrixData.completion_summary || {};
      const pendingReview = proposedFailed ? null : getTrainingOpenItems(proposedArray).length;

      setSummary({
        totalRequired: apiSummary.required_total ?? requiredItems.length,
        current: apiSummary.current ?? requiredItems.filter(isMandatoryTrainingSatisfied).length,
        needsRenewal: apiSummary.needsRenewal ?? requiredItems.filter(isTrainingDueSoon).length,
        missing: apiSummary.missing ?? requiredItems.filter(item => item.status === 'missing').length,
        blockers: apiSummary.blockers ?? 0,
        additionalQualifications: allQualifications.length,
        certificatesUploaded: certificatesFailed ? null : trainingCerts.length,
        needsReview: pendingReview
      });
      hasLoadedTrainingRef.current = true;
      setLastUpdatedAt(new Date());

    } catch (err) {
      console.error('Error fetching training data:', err);
      if (!silent || !hasLoadedTrainingRef.current) {
        setLoadError(true);
        setSourceErrors(prev => ({ ...prev, matrix: true }));
        toast.error('Failed to load training data');
      }
    } finally {
      if (silent && hasLoadedTrainingRef.current) {
        setIsRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  }, [employeeId, token]);

  const clearScheduledRefreshes = useCallback(() => {
    refreshTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    refreshTimersRef.current = [];
  }, []);

  const revalidateTrainingData = useCallback(() => {
    clearScheduledRefreshes();

    // First refresh immediately so the UI feels instant. Follow-up reads cover
    // storage/extraction/database propagation without making the admin reload.
    fetchTrainingData({ silent: true });
    [900, 2500].forEach((delay) => {
      const timer = window.setTimeout(() => {
        fetchTrainingData({ silent: true });
      }, delay);
      refreshTimersRef.current.push(timer);
    });

    // Let enclosing CareOps surfaces update their own cached summaries too.
    window.dispatchEvent(new CustomEvent('careops:training-updated', {
      detail: { employeeId },
    }));
    try {
      const parentRefresh = onRefresh?.();
      if (parentRefresh?.catch) parentRefresh.catch(() => {});
    } catch (err) {
      console.warn('Parent training refresh failed:', err);
    }
  }, [clearScheduledRefreshes, employeeId, fetchTrainingData, onRefresh]);

  const openWorkforceTrainingMatrix = (filter = 'all') => {
    const params = new URLSearchParams();
    if (filter && filter !== 'all') params.set('filter', filter);
    if (employeeId) params.set('employee_id', employeeId);
    const query = params.toString();
    window.location.assign(`/portal/training${query ? `?${query}` : ''}`);
  };

  const handleCapabilityProfileToggle = async (profileId, assigned) => {
    if (!profileId || capabilityMutating) return;
    setCapabilityMutating(profileId);
    try {
      if (assigned) {
        await axios.delete(
          `${API}/employees/${employeeId}/training/capability-profiles/${profileId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Service capability profile removed. Existing training evidence was not changed.');
      } else {
        await axios.post(
          `${API}/employees/${employeeId}/training/capability-profiles/${profileId}/assign`,
          { source_type: 'service' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Service capability profile enabled in advisory mode.');
      }
      revalidateTrainingData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update service capability profile');
    } finally {
      setCapabilityMutating(null);
    }
  };
  
  useEffect(() => {
    fetchTrainingData({ silent: false });
  }, [fetchTrainingData]);

  // A manager may review Adults/Children in the sibling Training Hub above.
  // Refresh THIS canonical matrix immediately, without incorrectly changing
  // verification status or waiting for the 30-second background refresh.
  useEffect(() => {
    const handleScopeReviewed = (event) => {
      if (!isSafeguardingScopeReviewForEmployee(
        event?.detail, employeeId, user?.workspace_id || user?.workspaceId || ''
      )) return;
      fetchTrainingData({ silent: true });
    };
    window.addEventListener('careops:safeguarding-scope-reviewed', handleScopeReviewed);
    return () => window.removeEventListener('careops:safeguarding-scope-reviewed', handleScopeReviewed);
  }, [employeeId, fetchTrainingData, user?.workspace_id, user?.workspaceId]);

  useEffect(() => {
    const refreshIfVisible = () => {
      if (document.visibilityState === 'visible') {
        fetchTrainingData({ silent: true });
      }
    };
    const interval = window.setInterval(refreshIfVisible, 30000);
    window.addEventListener('focus', refreshIfVisible);
    document.addEventListener('visibilitychange', refreshIfVisible);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshIfVisible);
      document.removeEventListener('visibilitychange', refreshIfVisible);
      clearScheduledRefreshes();
    };
  }, [clearScheduledRefreshes, fetchTrainingData]);

  // Open training detail drawer
  const handleOpenDetail = (item) => {
    // Force fresh drawer state so opening item B after closing item A
    // cannot render stale content from the previous selection.
    setDrawerOpen(false);
    setSelectedTraining(null);
    setTimeout(() => {
      setSelectedTraining(item);
      setDrawerOpen(true);
    }, 0);
  };

  // Open edit dialog for proposed item
  const handleEditProposed = (item) => {
    if (sourceErrors.proposedItems) {
      toast.error('Cannot assess pending reviews until training review data loads.');
      return;
    }
    setEditingItem(item);
    setNoExpiryChecked(!item?.expires_at && !item?.expiry_date);
    setEditDialogOpen(true);
  };

  const getSourceCertificateFileForProposedItem = (item) => {
    if (!item?.source_document_id) return null;
    return {
      id: item.source_document_id,
      file_id: item.source_document_id,
      file_name: item.source_document?.filename || 'Source training certificate',
      name: item.source_document?.filename || 'Source training certificate',
      uploaded_at: item.source_document?.uploaded_at,
    };
  };

  const getSourceCertificateFileForTrainingItem = (item) => {
    const evidenceFile = Array.isArray(item?.evidence_files) ? item.evidence_files[0] : null;
    const documentId = item?.source_document_id || evidenceFile?.document_id;
    if (!documentId) return null;
    return {
      id: documentId,
      file_id: documentId,
      file_name: item?.original_filename || evidenceFile?.original_filename || item?.title || 'Training certificate',
      name: item?.original_filename || evidenceFile?.original_filename || item?.title || 'Training certificate',
      uploaded_at: item?.uploaded_at || evidenceFile?.uploaded_at,
    };
  };

  const openTrainingEvidenceReview = (item) => {
    if (sourceErrors.proposedItems) {
      toast.error('Cannot assess pending reviews until training review data loads.');
      return;
    }
    if (item?.status !== 'proposed') {
      toast.info('This extracted training item has already been reviewed.');
      return;
    }
    if (!getSourceCertificateFileForProposedItem(item)) {
      toast.error('Source certificate is missing for this extracted item.');
      return;
    }
    setTrainingReviewItem(item);
    setTrainingReviewPurpose('proposed');
    setTrainingReviewOpen(true);
  };

  // Approve proposed item — now goes through the mandatory evidence-review
  // endpoint with an explicit VERIFY decision (records the human review AND
  // verifies server-side). There is no longer a direct AI->verified path.
  const handleApproveProposed = async (item, notes, overrides = {}) => {
    if (sourceErrors.proposedItems) {
      toast.error('Cannot assess pending reviews until training review data loads.');
      return;
    }
    try {
      const res = await axios.post(
        `${API}/employees/${employeeId}/training/proposed-items/${item.id}/evidence-review`,
        {
          decision: 'verify',
          mapped_training_code: overrides.mapped_training_code ?? item.mapped_training_code,
          mapped_training_title: overrides.mapped_training_title ?? item.mapped_training_title,
          completed_at: overrides.completed_at ?? item.completed_at,
          expires_at: overrides.expires_at ?? item.expires_at,
          provider_name: overrides.provider_name ?? item.provider_name,
          certificate_number: overrides.certificate_number ?? item.certificate_number,
          notes,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // HTTP 200 can mean "review recorded" even when verification failed.
      // Check the separate canonical outcome and leave the dialog open on error.
      const { renewed } = requireVerifiedTrainingReview(res.data);
      toast.success(renewed
        ? 'Renewal verified. Previous certificate preserved in history.'
        : 'Evidence reviewed and training verified');
      closeTrainingReview();
      revalidateTrainingData();
      return res.data;
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'Failed to verify after review';
      toast.error(message);
      // Do not falsely mark the review complete. The viewer will display
      // the actual reason and allow correction without repeating navigation.
      throw new Error(message);
    }
  };

  // Needs clarification — explicit third review decision. Does NOT verify,
  // keeps the source evidence + audit, and marks the proposal for follow-up.
  const handleNeedsClarification = async (item, notes = 'Needs clarification / clearer evidence') => {
    try {
      await axios.post(
        `${API}/employees/${employeeId}/training/proposed-items/${item.id}/evidence-review`,
        { decision: 'needs_clarification', notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.info('Marked as needs clarification — the applicant can provide clearer evidence.');
      closeTrainingReview();
      revalidateTrainingData();
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'Failed to update item';
      toast.error(message);
      throw new Error(message);
    }
  };

  // Reject proposed item — recorded as an explicit evidence-review decision.
  const handleRejectProposed = async (item, notes = 'Rejected by admin') => {
    if (sourceErrors.proposedItems) {
      toast.error('Cannot assess pending reviews until training review data loads.');
      return;
    }
    try {
      await axios.post(
        `${API}/employees/${employeeId}/training/proposed-items/${item.id}/evidence-review`,
        { decision: 'reject', notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Training item rejected');
      closeTrainingReview();
      revalidateTrainingData();
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'Failed to reject item';
      toast.error(message);
      throw new Error(message);
    }
  };

  // Quick-verify eligibility check (mirrors backend safety rules)
  const canQuickVerify = (item) => (
    Boolean(item?.source_document_id) &&         // source certificate present
    Boolean((item?.raw_course_title || '').trim()) &&  // extracted title present
    Boolean(item?.mapped_training_code || item?.mapped_training_title) && // mapped
    Boolean(item?.completed_at) &&               // completed date present
    !item?.is_unmapped                           // not flagged unmapped
  );

  // One-step approve+verify is retired. Verification must go through the
  // mandatory evidence-review flow, so this now simply opens that review.
  const handleApproveAndVerify = async (item) => {
    openTrainingEvidenceReview(item);
  };

  // Batch approve-and-verify is retired — a bulk action must never verify an
  // item that has not completed mandatory evidence review. Direct each selected
  // item into the individual review flow instead.
  const handleBatchApproveAndVerify = async () => {
    if (selectedForBatch.size === 0) return;
    const first = proposedItems.find((p) => p.status === 'proposed' && selectedForBatch.has(p.id));
    toast.info('Each item must be reviewed individually before it can be verified. Opening the first item for review.');
    if (first) openTrainingEvidenceReview(first);
  };

  const handleBatchReject = async () => {
    if (selectedForBatch.size === 0) return;
    // No bulk dismissal of compliance evidence without source-linked review.
    const first = proposedItems.find((p) => p.status === 'proposed' && selectedForBatch.has(p.id));
    toast.info('Review each proposed course individually and record a reason before rejecting. Opening the first item.');
    if (first) openTrainingEvidenceReview(first);
  };

  const toggleBatchSelect = (itemId) => {
    setSelectedForBatch(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const selectAllForBatch = (items) => {
    setSelectedForBatch(new Set(items.map(i => i.id)));
  };

  const clearBatchSelection = () => setSelectedForBatch(new Set());

  // Delete training record - Admin only with audit trail
  const handleDeleteTraining = async () => {
    if (!deletingItem) return;

    setDeleting(true);
    try {
      const linkedBySourceDoc = (Array.isArray(canonicalTrainingRecords) ? canonicalTrainingRecords : [])
        .filter((row) => row?.source_document_id && row.source_document_id === deletingItem?.source_document_id);
      const targets = deleteLinkedRecords && linkedBySourceDoc.length > 1
        ? linkedBySourceDoc
        : [deletingItem];
      const targetIds = Array.from(new Set(targets.map((row) => row?.record_id || row?.id).filter(Boolean)));
      if (targetIds.length === 0) {
        toast.error('No record ID found for this training selection');
        return;
      }
      const results = await Promise.allSettled(
        targetIds.map((recordId) => axios.delete(
          `${API}/training-records/${recordId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { reason: deleteReason || 'Deleted by admin' }
          }
        ))
      );
      const deletedCount = results.filter((r) => r.status === 'fulfilled').length;
      if (deletedCount === 0) {
        throw new Error('No training records were deleted');
      }
      toast.success(
        deleteLinkedRecords && targetIds.length > 1
          ? `Deleted ${deletedCount} linked training record(s)`
          : `"${deletingItem.title}" deleted successfully`
      );
      setDeleteDialogOpen(false);
      setDeletingItem(null);
      setDeleteReason('');
      setDeleteLinkedRecords(false);
      revalidateTrainingData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete training');
    } finally {
      setDeleting(false);
    }
  };
  
  // Open delete confirmation
  const openDeleteDialog = (item) => {
    setDeletingItem(item);
    setDeleteReason('');
    setDeleteLinkedRecords(false);
    setDeleteDialogOpen(true);
  };

  const submitVerifyTraining = async (item) => {
    await axios.post(
      `${API}/employees/${employeeId}/training/${item.code || item.id}/verify`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    toast.success(`${item.title} verified`);
    closeTrainingReview();
    revalidateTrainingData();
  };

  // Verify training record - Admin only
  const handleVerifyTraining = async (item) => {
    try {
      await submitVerifyTraining(item);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to verify training');
    }
  };

  const openTrainingVerifyReview = (item) => {
    if (isTrainingVerified(item)) {
      toast.info(`${item.title} is already verified`);
      return;
    }
    const sourceFile = getSourceCertificateFileForTrainingItem(item);
    if (!sourceFile) {
      handleVerifyTraining(item);
      return;
    }
    setTrainingReviewItem(item);
    setTrainingReviewPurpose('verify');
    setTrainingReviewOpen(true);
  };

  // Unverify training record - Admin only, requires reason
  const handleUnverifyTraining = async (item) => {
    setUnverifyItem(item);
    setUnverifyReason('');
    setUnverifyDialogOpen(true);
  };

  const confirmUnverifyTraining = async () => {
    if (!unverifyItem || !unverifyReason.trim()) return;

    setUnverifying(true);
    try {
      await axios.post(
        `${API}/employees/${employeeId}/training/${unverifyItem.code || unverifyItem.id}/unverify`,
        { reason: unverifyReason.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${unverifyItem.title} unverified`);
      setUnverifyDialogOpen(false);
      setUnverifyItem(null);
      setUnverifyReason('');
      revalidateTrainingData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unverify training');
    } finally {
      setUnverifying(false);
    }
  };

  // Re-run extraction on certificate
  const handleReExtract = async (documentId) => {
    if (sourceErrors.certificates) {
      toast.error('Cannot assess certificates until certificate data loads.');
      return;
    }
    try {
      const response = await axios.post(
        `${API}/employees/${employeeId}/training/re-extract`,
        { document_id: documentId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success && response.data.trainings?.length > 0) {
        // If AI was skipped because items already exist, just inform the admin
        if (response.data.skipped_ai) {
          toast.info(response.data.message || 'Items already extracted — check Training Library for pending reviews');
          revalidateTrainingData();
          return;
        }
        // Auto-submit extracted items as proposed for review
        const trainingsToSave = response.data.trainings
          .filter(t => !t.already_proposed)
          .map(t => ({ ...t, document_id: documentId }));
        if (trainingsToSave.length > 0) {
          await axios.post(
            `${API}/employees/${employeeId}/training/bulk-save`,
            { trainings: trainingsToSave },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          toast.success(`Extracted ${trainingsToSave.length} training(s) — submitted for review`);
        } else {
          toast.info('All extracted trainings already pending review — check Training Library tab');
        }
        revalidateTrainingData();
      } else {
        toast.error(response.data.message || 'No trainings detected');
        revalidateTrainingData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to extract training');
      revalidateTrainingData();
    }
  };

  // Remove certificate file + extracted items
  const handleRemoveCertificate = async () => {
    if (!removeCertDialogCert) return;
    setRemovingCert(true);
    try {
      const res = await axios.delete(
        `${API}/employees/${employeeId}/training/certificates/${removeCertDialogCert.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const preserved = res.data?.approved_training_records_preserved || 0;
      const deleted = res.data?.proposed_items_deleted || 0;
      toast.success(
        preserved > 0
          ? `Certificate removed. ${deleted} extracted item(s) deleted. ${preserved} approved training record(s) preserved.`
          : `Certificate removed${deleted > 0 ? `. ${deleted} extracted item(s) deleted.` : '.'}`
      );
      setRemoveCertDialogCert(null);
      revalidateTrainingData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove certificate.');
    } finally {
      setRemovingCert(false);
    }
  };

  // Render status badge
  const renderStatusBadge = (status) => {
    const style = STATUS_STYLES[status] || STATUS_STYLES.missing;
    const Icon = style.icon;
    return (
      <Badge className={cn('text-xs', style.bg, style.text, style.border)}>
        <Icon className="h-3 w-3 mr-1" />
        {style.label}
      </Badge>
    );
  };

  const renderExtractionStatusBadge = (cert) => {
    const status = cert.extraction_status || (cert.extraction_count || cert.extracted_item_count ? 'extracted_with_matches' : 'not_extracted');
    const extractedCount = cert.extracted_item_count ?? cert.extraction_count ?? 0;
    const statusConfig = {
      not_extracted: { label: 'Not extracted', className: 'bg-gray-100 text-gray-700 border-gray-200', icon: Clock },
      extraction_pending: { label: 'Extraction pending', className: 'bg-blue-50 text-blue-700 border-blue-200', icon: Loader2 },
      extraction_failed: { label: 'Extraction failed', className: 'bg-red-50 text-red-700 border-red-200', icon: AlertTriangle },
      extracted_no_match: { label: 'Extracted: no matches', className: 'bg-amber-50 text-amber-700 border-amber-200', icon: AlertTriangle },
      extracted_with_matches: { label: `Extracted: ${extractedCount} item${extractedCount === 1 ? '' : 's'}`, className: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
      completed: { label: `Extracted: ${extractedCount} item${extractedCount === 1 ? '' : 's'}`, className: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
    };
    const config = statusConfig[status] || statusConfig.not_extracted;
    const Icon = config.icon;

    return (
      <Badge variant="outline" className={cn('text-xs', config.className)}>
        <Icon className={cn('h-3 w-3 mr-1', status === 'extraction_pending' && 'animate-spin')} />
        {config.label}
      </Badge>
    );
  };

  // Progress calculation
  const progressPercent = summary.totalRequired > 0
    ? Math.round((summary.current / summary.totalRequired) * 100)
    : 0;
  const mandatoryVerifiedCurrent = mandatoryTraining.filter(isMandatoryTrainingSatisfied);
  const mandatoryMissing = mandatoryTraining.filter(item => item.status === 'missing');
  const mandatoryDueSoon = mandatoryTraining.filter(isTrainingDueSoon);
  const mandatoryExpired = mandatoryTraining.filter(isTrainingExpiredOrOverdue);
  const mandatoryPendingVerification = mandatoryTraining.filter(item =>
    !isMandatoryTrainingSatisfied(item) &&
    !REJECTED_STATUSES.has(item.status) &&
    !isTrainingExpiredOrOverdue(item) &&
    !isTrainingDueSoon(item) &&
    (PENDING_REVIEW_STATUSES.has(item.status) || (isEvidenceOnFile(item) && !isTrainingVerified(item)))
  );
  const mandatoryBlockers = mandatoryTraining.filter(item =>
    item.status === 'missing' ||
    REJECTED_STATUSES.has(item.status) ||
    isTrainingExpiredOrOverdue(item) ||
    !isMandatoryTrainingSatisfied(item)
  );
  const exactBlockerNames = mandatoryBlockers
    .map(item => item.title || item.training_name || item.code || 'Unnamed training')
    .filter(Boolean);
  const normalizeTrainingKey = (value) => (
    (value || '')
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[()]/g, ' ')
      .replace(/[\s_-]+/g, ' ')
      .trim()
  );
  const normalizeTrainingAliasKey = (value) => {
    const base = normalizeTrainingKey(value)
      .replace(/\bonline training\b/g, '')
      .replace(/\btraining module\b/g, '')
      .replace(/\bfor volunteers\b/g, '')
      .replace(/\bupdated covid ?19\b/g, '')
      .replace(/\blevel\b/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    const aliasMap = {
      'ils immediate life support': 'immediate life support',
      'cstf adult immediate life support': 'immediate life support',
      'cstf paediatric immediate life support': 'immediate life support',
      'information governance and data security gdpr': 'information governance',
      'information governance and gdpr': 'information governance',
      'infection prevention and control': 'infection control',
      'health safety and infection prevention and control': 'infection control',
      'basic life support pbls': 'basic life support',
      'sepsis awareness': 'sepsis awareness',
      'manual handling': 'manual handling',
      'fire safety awareness': 'fire safety',
      'food hygiene safety': 'food hygiene',
      'equality diversity and human rights': 'equality and diversity',
      'dignity and privacy': 'privacy and dignity',
      'news score training': 'news2 clinical observations',
      'news2 additional guidance': 'news2 clinical observations',
    };
    return aliasMap[base] || base;
  };
  const getPendingExtractedMatch = (mandatoryItem) => {
    const mandatoryCandidates = [
      mandatoryItem?.code,
      mandatoryItem?.title,
      mandatoryItem?.id,
      mandatoryItem?.training_name,
    ].map(normalizeTrainingAliasKey).filter(Boolean);

    return proposedItems.find((proposed) => {
      if (proposed.status !== 'proposed') return false;
      const proposedCandidates = [
        proposed.mapped_training_code,
        proposed.mapped_training_title,
        proposed.raw_course_title,
        proposed.training_name,
        proposed.course_name,
      ].map(normalizeTrainingAliasKey).filter(Boolean);

      return mandatoryCandidates.some((mandatoryKey) =>
        proposedCandidates.some((proposedKey) =>
          proposedKey === mandatoryKey ||
          proposedKey.includes(mandatoryKey) ||
          mandatoryKey.includes(proposedKey)
        )
      );
    });
  };
  const mandatoryKeys = new Set(
    mandatoryTraining.map(item => normalizeTrainingKey(item.code || item.title || item.id))
  );
  const isRequiredTrainingItem = (item) => {
    const key = normalizeTrainingKey(item?.code || item?.title || item?.id);
    return Boolean(item?.is_required || item?.is_mandatory || item?.required || item?.mandatory || mandatoryKeys.has(key));
  };
  const cannotAssessCount = (sourceErrors.certificates ? 1 : 0) + (sourceErrors.proposedItems ? 1 : 0) + (sourceErrors.trainingRecords ? 1 : 0);
  // Never silently drop saved proposal IDs just because the title/source matches.
  const pendingProposedItems = getTrainingReviewQueue(proposedItems);
  const proposalReviewSummary = summarizeTrainingReviewQueue(proposedItems);
  const trainingMappingOptions = (() => {
    const seen = new Set();
    const out = [];
    const add = (code, title) => {
      const c = String(code || '').trim();
      const t = String(title || '').trim();
      if (!c && !t) return;
      const key = `${c.toLowerCase()}|${t.toLowerCase()}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push({ code: c || t, title: t || c });
    };
    (Array.isArray(mandatoryTraining) ? mandatoryTraining : []).forEach((item) => {
      add(item?.code || item?.id, item?.title || item?.training_name);
    });
    (Array.isArray(canonicalTrainingRecords) ? canonicalTrainingRecords : []).forEach((item) => {
      add(item?.code || item?.requirement_id || item?.id, item?.title || item?.training_name);
    });
    return out.sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')));
  })();
  // Keep each original certificate visible, even when filenames/dates coincide.
  const followUpItems = getTrainingFollowUpQueue(proposedItems);
  const openTrainingItems = getTrainingOpenItems(proposedItems);
  const sourceFollowUpItems = reviewSourceDocumentId
    ? followUpItems.filter(item => String(item.source_document_id || '') === String(reviewSourceDocumentId))
    : followUpItems;
  const groupedCertificates = getCertificateEvidenceRows(certificates, proposedItems);
  const trainingLibraryBanner = getTrainingLibraryBannerState({
    proposedItems,
    proposedItemsErrored: sourceErrors.proposedItems,
  });
  let trainingDecisionState;
  if (loadError) {
    trainingDecisionState = 'Cannot assess';
  } else if (sourceErrors.matrix || sourceErrors.certificates || sourceErrors.proposedItems || sourceErrors.trainingRecords) {
    // Any missing authoritative evidence/review feed means readiness cannot
    // be independently reconciled. Never claim "Training ready" from partial data.
    trainingDecisionState = 'Cannot assess';
  } else if (mandatoryTraining.length === 0) {
    // No mandatory requirements present -> readiness cannot be truthfully asserted.
    trainingDecisionState = 'Cannot assess';
  } else if (mandatoryTraining.every(isMandatoryTrainingSatisfied)) {
    // Training ready ONLY when EVERY required mandatory item is verified/current.
    trainingDecisionState = 'Training ready';
  } else {
    // Any mandatory item that is expired/missing/rejected/awaiting/etc. blocks readiness.
    trainingDecisionState = 'Blocked';
  }
  const openTrainingRequestDialog = () => {
    setRequestDialogDefaults(getTrainingRenewalRequestDefaults(mandatoryTraining));
    setRequestDialogOpen(true);
  };

  const getDisplayStatus = (item) => {
    const pendingExtractedMatch = !isMandatoryTrainingSatisfied(item)
      ? getPendingExtractedMatch(item)
      : null;
    if (item?.status === 'missing' && pendingExtractedMatch) {
      return 'pending';
    }
    return item?.status;
  };

  const qualificationGroups = partitionCanonicalQualifications(canonicalTrainingRecords, {
    isRequired: isRequiredTrainingItem,
    getStatus: getDisplayStatus,
  });

  const trainingDecisionClasses = trainingDecisionState === 'Training ready'
    ? {
        panel: 'border-emerald-200 bg-emerald-50',
        icon: 'text-emerald-600',
        text: 'text-emerald-800',
        subtext: 'text-emerald-700',
      }
    : trainingDecisionState === 'Cannot assess'
      ? {
          panel: 'border-red-200 bg-red-50',
          icon: 'text-red-600',
          text: 'text-red-800',
          subtext: 'text-red-700',
        }
      : {
          panel: 'border-amber-200 bg-amber-50',
          icon: 'text-amber-600',
          text: 'text-amber-800',
          subtext: 'text-amber-700',
        };
  if (loadError) {
    return (
      <Card className="border-red-200" data-testid="training-matrix-error">
        <CardContent className="text-center py-12 text-red-700">
          <AlertTriangle className="h-10 w-10 mx-auto mb-3 text-red-500" />
          <p className="font-medium">Cannot assess training</p>
          <p className="text-sm mt-1">Training data unavailable. Upload, verify, and review actions are disabled until this source loads.</p>
          <Button variant="outline" size="sm" onClick={fetchTrainingData} className="mt-4">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="audit-ready-training-matrix">
      {dependencyWarnings.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <p className="font-medium">Training dependency issues</p>
          <ul className="list-disc pl-5 mt-1">
            {dependencyWarnings.map((w, i) => (
              <li key={i}>{w.message}</li>
            ))}
          </ul>
        </div>
      )}
      {/* Dependency Warnings */}
      {dependencyWarnings && dependencyWarnings.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <strong>Dependency Warnings:</strong>
          <ul className="list-disc pl-5 mt-1">
            {dependencyWarnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}
      <div className={`rounded-lg border p-4 ${trainingDecisionClasses.panel}`}>
        <div className="flex items-start gap-3">
          <div className={`mt-0.5 ${trainingDecisionClasses.icon}`}>
            {trainingDecisionState === 'Training ready' ? (
              <CheckCircle2 className="h-5 w-5" />
            ) : (
              <AlertTriangle className="h-5 w-5" />
            )}
          </div>
          <div className="flex-1">
            <p className={`font-medium ${trainingDecisionClasses.text}`}>
              Training status: {trainingDecisionState}
            </p>
            <p className={`mt-1 text-sm ${trainingDecisionClasses.subtext}`}>
              Only verified and current mandatory training counts toward work readiness.
            </p>
            <p className={`mt-1 text-sm ${trainingDecisionClasses.subtext}`}>
              Certificates are evidence only. Extracted items must be reviewed into training records before required training status changes.
            </p>
            {trainingDecisionState === 'Blocked' && exactBlockerNames.length > 0 && (
              <div className={`mt-2 text-sm ${trainingDecisionClasses.subtext}`}>
                <p className="font-medium">Required training still needed</p>
                <ul className="mt-1 list-disc space-y-1 pl-5">
                  {exactBlockerNames.slice(0, 8).map((name) => (
                    <li key={name}>{name}</li>
                  ))}
                  {exactBlockerNames.length > 8 && (
                    <li>{exactBlockerNames.length - 8} more mandatory item{exactBlockerNames.length - 8 !== 1 ? 's' : ''}</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* SUMMARY CARDS                               */}
      {/* ============================================ */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-10 gap-3">
        <div className="p-3 bg-white border border-gray-200 rounded-lg">
          <p className="text-2xl font-bold text-gray-900">{summary.totalRequired}</p>
          <p className="text-xs text-gray-500">Required</p>
        </div>
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
          <p className="text-2xl font-bold text-emerald-700">{mandatoryVerifiedCurrent.length}</p>
          <p className="text-xs text-emerald-600">Verified/current</p>
        </div>
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-2xl font-bold text-amber-700">{mandatoryDueSoon.length}</p>
          <p className="text-xs text-amber-600">Due soon</p>
        </div>
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-2xl font-bold text-red-700">{mandatoryExpired.length}</p>
          <p className="text-xs text-red-600">Expired/overdue</p>
        </div>
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-2xl font-bold text-red-700">{mandatoryMissing.length}</p>
          <p className="text-xs text-red-600">Missing</p>
        </div>
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className={cn('font-bold text-blue-700', sourceErrors.trainingRecords ? 'text-sm' : 'text-2xl')}>
            {sourceErrors.trainingRecords ? 'Cannot assess' : summary.additionalQualifications}
          </p>
          <p className="text-xs text-blue-600">Approved</p>
        </div>
        <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
          <p className={cn('font-bold text-slate-700', sourceErrors.certificates ? 'text-sm' : 'text-2xl')}>
            {sourceErrors.certificates ? 'Cannot assess' : summary.certificatesUploaded}
          </p>
          <p className="text-xs text-slate-600">Certificates</p>
        </div>
        <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
          <p className={cn('font-bold text-purple-700', sourceErrors.proposedItems ? 'text-sm' : 'text-2xl')}>
            {sourceErrors.proposedItems ? 'Cannot assess' : summary.needsReview}
          </p>
          <p className="text-xs text-purple-600">Open review / follow-up</p>
        </div>
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-2xl font-bold text-blue-700">{mandatoryPendingVerification.length}</p>
          <p className="text-xs text-blue-600">Pending verification</p>
        </div>
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-2xl font-bold text-red-700">{cannotAssessCount}</p>
          <p className="text-xs text-red-600">Cannot assess</p>
        </div>
        <div className="p-3 bg-gray-100 border border-gray-200 rounded-lg">
          <p className="text-2xl font-bold text-gray-700">{progressPercent}%</p>
          <p className="text-xs text-gray-500">Current</p>
        </div>
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
        <span className="font-medium">Required training still needed:</span> {mandatoryBlockers.length} &nbsp;|&nbsp;
        <span className="font-medium">Pending verification:</span> {mandatoryPendingVerification.length} &nbsp;|&nbsp;
        <span className="font-medium">Expired/overdue:</span> {mandatoryExpired.length} &nbsp;|&nbsp;
        <span className="font-medium">Cannot assess:</span> {cannotAssessCount} &nbsp;|&nbsp;
        <span className="font-medium">Verified/current:</span> {mandatoryVerifiedCurrent.length}/{summary.totalRequired}
        {sourceErrors.certificates && <span> | Cannot assess certificates</span>}
        {sourceErrors.proposedItems && <span> | Cannot assess pending reviews</span>}
        {sourceErrors.trainingRecords && <span> | Cannot assess approved qualifications</span>}
      </div>

      {/* Progress Bar */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Mandatory Training Current</span>
          <span className="text-sm font-bold">{progressPercent}%</span>
        </div>
        <Progress 
          value={progressPercent}
          className={cn(
            "h-3",
            progressPercent >= 80 ? "[&>div]:bg-emerald-500" :
            progressPercent >= 50 ? "[&>div]:bg-amber-500" :
            "[&>div]:bg-red-500"
          )}
        />
        {summary.blockers > 0 && (
          <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <span className="text-sm text-red-800">
              {summary.blockers} work-blocking item{summary.blockers !== 1 ? 's' : ''} require attention
            </span>
          </div>
        )}
      </div>

      {/* ============================================ */}
      {/* TABBED SECTIONS                             */}
      {/* ============================================ */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 lg:w-auto lg:inline-flex">
          <TabsTrigger value="mandatory" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Mandatory ({summary.totalRequired})
          </TabsTrigger>
          <TabsTrigger value="library" className="flex items-center gap-2">
            <Book className="h-4 w-4" />
            All Qualifications ({sourceErrors.trainingRecords ? 'Cannot assess' : canonicalTrainingRecords.length}){!sourceErrors.proposedItems && openTrainingItems.length > 0 ? ` · ${openTrainingItems.length} open` : ''}
          </TabsTrigger>
          <TabsTrigger value="certificates" className="flex items-center gap-2">
            <Award className="h-4 w-4" />
            Certificates ({sourceErrors.certificates ? 'Cannot assess' : summary.certificatesUploaded})
          </TabsTrigger>
          <TabsTrigger value="service_readiness" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Service Readiness
          </TabsTrigger>
        </TabsList>

        {/* MANDATORY TRAINING TAB */}
        <TabsContent value="mandatory" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Shield className="h-5 w-5 text-primary" />
                    Mandatory Training Requirements
                  </CardTitle>
                  <CardDescription>
                    Mandatory only reflects approved training records. Extracted certificate evidence must be reviewed and then verified before it can satisfy work readiness.
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  {isAdmin && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={openTrainingRequestDialog}
                        data-testid="request-training-certs-btn"
                      >
                        <Mail className="h-4 w-4 mr-1" />
                        Request Certificates
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setExtractorOpen(true)}
                        data-testid="upload-training-cert-btn"
                      >
                        <Upload className="h-4 w-4 mr-1" />
                        Upload Certificate
                      </Button>
                    </>
                  )}
                  <div className="hidden sm:flex items-center gap-1.5 text-xs text-slate-500" aria-live="polite">
                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                    <span>{isRefreshing ? 'Syncing…' : 'Live'}</span>
                    {lastUpdatedAt && !isRefreshing && (
                      <span className="text-slate-400">
                        · {lastUpdatedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => fetchTrainingData({ silent: true })}
                    disabled={isRefreshing}
                    title="Refresh training data"
                    aria-label="Refresh training data"
                  >
                    <RefreshCw className={cn("h-4 w-4", isRefreshing && "animate-spin")} />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[250px]">Training</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Certificate(s)</TableHead>
                    <TableHead>Completed</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(Array.isArray(mandatoryTraining) ? mandatoryTraining : []).map((item) => {
                    const pendingExtractedMatch = !isMandatoryTrainingSatisfied(item)
                      ? getPendingExtractedMatch(item)
                      : null;
                    return (
                      <TableRow
                        key={item.code || item.id}
                        className={cn(item.blocker && 'bg-red-50/50')}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {item.is_currently_blocking && (
                              <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0" />
                            )}
                            <div>
                              <p className="font-medium text-gray-900">{item.title}</p>

                              {item.code === 'safeguarding' && (
                                <div className="mt-1" data-testid="mandatory-safeguarding-coverage">
                                  {verifiedSafeguardingCoverage(item).length ? (
                                    <div>
                                      <p className="mb-1 text-xs text-slate-500">Verified evidence covers these subjects (requirements depend on role):</p>
                                      <div className="flex flex-wrap items-center gap-1.5">
                                      {verifiedSafeguardingCoverage(item).map(subject => (
                                        <span key={subject.code} className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs text-emerald-800">
                                          <CheckCircle2 className="h-3 w-3" />{subject.label}
                                        </span>
                                      ))}
                                      </div>
                                    </div>
                                  ) : (
                                    <p className="text-xs text-slate-600">
                                      {safeguardingReviewedScope(item)
                                        ? `Reviewed subject: ${item.safeguarding_scope_label || safeguardingReviewedScope(item)} · Current work coverage not yet confirmed`
                                        : (item.safeguarding_scope_label || 'Safeguarding coverage needs review')}
                                      {!item.accepted_by_admin ? ' · verify evidence before clearance' : ''}
                                    </p>
                                  )}
                                </div>
                              )}
                              {item.renewal_pending && (
                                <p className="text-xs text-purple-700">
                                  Renewal received · awaiting admin verification
                                </p>
                              )}
                              {item.is_currently_blocking && (
                                <p className="text-xs text-red-600">Work blocker</p>
                              )}
                              {pendingExtractedMatch && (
                                <p className="text-xs text-purple-700">
                                  Matching extracted evidence awaiting admin review
                                </p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {verifiedSafeguardingNeedsScopeReview(item, isMandatoryTrainingSatisfied(item)) ? (
                            <Badge className="border border-amber-300 bg-amber-50 text-amber-900 text-xs"
                              data-testid="safeguarding-subject-review-status">
                              <AlertTriangle className="mr-1 h-3 w-3" />
                              Verified certificate · subject review needed
                            </Badge>
                          ) : renderStatusBadge(getDisplayStatus(item))}
                        </TableCell>
                        <TableCell>
                          {(item.record_id || item.id) && (item.source_document_id || item.certificate_url || item.evidence_files?.length) ? (
                            <Button
                              variant="link"
                              size="sm"
                              className="h-auto p-0 text-blue-600"
                              onClick={() => onViewCertificate?.(item.record_id || item.id)}
                            >
                              <FileText className="h-3 w-3 mr-1" />
                              Open certificate
                            </Button>
                          ) : item.certificate_url ? (
                            <a
                              href={item.certificate_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-sm text-blue-600 hover:underline"
                            >
                              <FileText className="h-3 w-3 mr-1" />
                              Open certificate
                            </a>
                          ) : item.evidence?.length > 0 ? (
                            <div className="flex items-center gap-1">
                              <FileText className="h-4 w-4 text-gray-400" />
                              <span className="text-sm text-gray-600">
                                {item.evidence.length} file{item.evidence.length !== 1 ? 's' : ''}
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">None</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-gray-600">
                            {item.completed_at ? formatBackendDate(item.completed_at, { format: 'short' }) : '-'}
                          </span>
                        </TableCell>
                        <TableCell>
                          {item.expires_at ? (
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className={cn(
                                "text-sm",
                                getDisplayStatus(item) === 'expired' ? 'text-red-600 font-medium' :
                                getDisplayStatus(item) === 'expiring_soon' ? 'text-amber-600' :
                                'text-gray-600'
                              )}>
                                {formatBackendDate(item.expires_at, { format: 'short' })}
                              </span>
                              <DaysRemainingBadge expiresAt={item.expires_at} />
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {/* Details are not another certificate viewer. */}
                            {item.completed_at && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-9 gap-1 px-2"
                                onClick={() => handleOpenDetail(item)}
                                aria-label="Open training record details and history"
                                title="Open training record details and history"
                                data-testid={`mandatory-record-details-${item.code || item.id}`}
                              >
                                <MoreHorizontal className="h-4 w-4" />
                                <span>Details</span>
                              </Button>
                            )}
                            {/* Edit button for Admin */}
                            {isAdmin && item.completed_at && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-gray-500 hover:text-primary"
                                onClick={() => {
                                  setEditingItem(item);
                                  setEditDialogOpen(true);
                                }}
                                title="Edit dates"
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                            )}
                            {isAdmin &&
                              !item.renewal_pending &&
                              ['missing', 'expired', 'rejected', 'due_soon', 'expiring_soon'].includes(getDisplayStatus(item)) && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8"
                                onClick={() => setExtractorOpen(true)}
                              >
                                <Upload className="h-3.5 w-3.5 mr-1" />
                                {['expired', 'due_soon', 'expiring_soon'].includes(getDisplayStatus(item))
                                  ? 'Upload renewal'
                                  : 'Upload'}
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TRAINING LIBRARY TAB */}
        <TabsContent value="library" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Book className="h-5 w-5 text-primary" />
                    Training Library
                  </CardTitle>
                  <CardDescription>
                    This tab separates approved/canonical qualifications from extracted certificate items awaiting admin review.
                  </CardDescription>
                </div>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search training..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className={cn(
                'mb-4 rounded-lg p-3 text-sm',
                trainingLibraryBanner.tone === 'instructional' && 'border border-blue-200 bg-blue-50 text-blue-800',
                trainingLibraryBanner.tone === 'clear' && 'border border-emerald-200 bg-emerald-50 text-emerald-800',
                trainingLibraryBanner.tone === 'attention' && 'border border-amber-200 bg-amber-50 text-amber-900',
                trainingLibraryBanner.tone === 'error' && 'border border-red-200 bg-red-50 text-red-700',
              )}>
                <p className="font-medium">{trainingLibraryBanner.title}</p>
                <p className="mt-1">
                  {trainingLibraryBanner.body}
                </p>
              </div>
              {reviewSourceDocumentId && !sourceErrors.proposedItems && (
                <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2" data-testid="source-review-filter">
                  <span className="text-sm text-indigo-900">
                    Showing {getSourceReviewQueue(pendingProposedItems, reviewSourceDocumentId).length + sourceFollowUpItems.length} unresolved item(s) from this certificate.
                  </span>
                  <Button size="sm" variant="outline" onClick={() => setReviewSourceDocumentId(null)} data-testid="show-all-training-reviews">
                    Show all {openTrainingItems.length} items
                  </Button>
                </div>
              )}
              {!sourceErrors.proposedItems && sourceFollowUpItems.length > 0 && (
                <div className="mb-5 rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="training-follow-up-queue">
                  <p className="font-medium text-amber-950">Follow-up required ({sourceFollowUpItems.length}) — not verified</p>
                  <p className="mt-1 text-xs text-amber-900">These saved items need clarification or a reviewer status check. No automatic approval, re-upload or work-readiness clearance.</p>
                  <ul className="mt-3 space-y-2">
                    {sourceFollowUpItems.map(item => (
                      <li key={item.id} className="flex flex-wrap items-center justify-between gap-2 border-t border-amber-200 pt-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-amber-950">{item.raw_course_title || item.mapped_training_title || 'Unmapped course'}</p>
                          <p className="text-xs text-amber-800">Status: {String(item.status || '').replace(/_/g, ' ')} · {item.source_document?.filename || 'Source evidence unavailable'}</p>
                        </div>
                        {item.source_document_id && (
                          <Button variant="outline" size="sm" onClick={() => setCertificatePreview({
                            id: item.source_document_id,
                            filename: item.source_document?.filename || 'Source training certificate',
                          })} data-testid={`follow-up-view-source-${item.id}`}>
                            <Eye className="h-3 w-3 mr-1" /> View source
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {(() => {
                const pendingItems = getSourceReviewQueue(pendingProposedItems, reviewSourceDocumentId);
                if (pendingItems.length === 0) return null;

                // Group by source_document_id to expose batch opportunities
                const byDoc = pendingItems.reduce((acc, item) => {
                  const key = item.source_document_id || '__none__';
                  if (!acc[key]) acc[key] = [];
                  acc[key].push(item);
                  return acc;
                }, {});
                const multiDocKeys = Object.keys(byDoc).filter(k => byDoc[k].length > 1 && k !== '__none__');

                return (
                  <div className="mb-6">
                    {/* Header + batch bar */}
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-purple-800 flex items-center gap-2">
                        <Wand2 className="h-4 w-4" />
                        Extracted items awaiting admin review ({pendingItems.length})
                      </h4>
                      {isAdmin && pendingItems.length > 1 && (
                        <div className="flex items-center gap-2">
                          {selectedForBatch.size > 0 ? (
                            <>
                              <span className="text-xs text-purple-700">{selectedForBatch.size} selected</span>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
                                onClick={clearBatchSelection}
                              >
                                Clear
                              </Button>
                              <Button
                                size="sm"
                                className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
                                disabled={batchVerifying || sourceErrors.proposedItems}
                                onClick={handleBatchApproveAndVerify}
                                data-testid="batch-review-evidence-btn"
                              >
                                {batchVerifying ? (
                                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                ) : (
                                  <Eye className="h-3 w-3 mr-1" />
                                )}
                                Review selected
                              </Button>
                            </>
                          ) : (
                            multiDocKeys.length > 0 && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 text-xs text-purple-700"
                                onClick={() => selectAllForBatch(pendingItems)}
                              >
                                Select all
                              </Button>
                            )
                          )}
                        </div>
                      )}
                    </div>
                    {proposalReviewSummary.repeatedEntries > 0 && (
                      <p className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-2 text-sm text-amber-900" data-testid="training-repeat-proposals-explained">
                        {proposalReviewSummary.total} saved proposals across {proposalReviewSummary.sourceSubjectGroups} source/subject groups. {proposalReviewSummary.repeatedEntries} entries repeat a mapped subject; they are kept visible because each saved item needs an individual human decision. No certificate is automatically replaced.
                      </p>
                    )}
                    <p className="mb-3 text-sm text-purple-700">
                      Open each item and compare it with the source evidence before choosing
                      <span className="font-medium"> Verify</span>,
                      <span className="font-medium"> Needs clarification</span> or
                      <span className="font-medium"> Reject</span>.
                    </p>

                    <div className="space-y-2">
                      {pendingItems.map((item) => {
                        const quickOk = canQuickVerify(item);
                        const isVerifying = quickVerifying[item.id];
                        const isSelected = selectedForBatch.has(item.id);
                        const sourceDocId = item?.source_document_id || null;
                        const normalizedMapped = normalizeTrainingAliasKey(item?.mapped_training_title || item?.mapped_training_code || '');
                        const likelyDuplicateApproved = !!(
                          sourceDocId &&
                          normalizedMapped &&
                          (Array.isArray(canonicalTrainingRecords) ? canonicalTrainingRecords : []).some((row) => {
                            if ((row?.source_document_id || null) !== sourceDocId) return false;
                            const rowNorm = normalizeTrainingAliasKey(row?.title || row?.code || '');
                            return !!rowNorm && rowNorm === normalizedMapped;
                          })
                        );
                        return (
                          <div
                            key={item.id}
                            className={cn(
                              'p-3 border rounded-lg',
                              isSelected
                                ? 'bg-emerald-50 border-emerald-300'
                                : 'bg-purple-50 border-purple-200'
                            )}
                          >
                            {(() => {
                              const exp = item.expiry_date || item.expires_at;
                              const isExpired = exp && new Date(exp) < new Date();
                              return isExpired ? (
                                <div className="mb-2 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700" data-testid="expired-evidence-note">
                                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                                  <span>Expired evidence — reviewing this confirms the historical record only and will not satisfy current compliance.</span>
                                </div>
                              ) : null;
                            })()}
                            <div className="flex items-start gap-3">
                              {/* Batch checkbox */}
                              {isAdmin && pendingItems.length > 1 && (
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleBatchSelect(item.id)}
                                  className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600 cursor-pointer focus:ring-emerald-500"
                                />
                              )}

                              {/* Info */}
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-purple-900 truncate">{item.raw_course_title}</p>
                                <p className="text-xs text-purple-600 mt-0.5">
                                  {item.mapped_training_code
                                    ? `Mapped → ${item.mapped_training_title || item.mapped_training_code}`
                                    : <span className="text-amber-600 font-medium">Unmapped — needs correction</span>}
                                  {item.completed_at && ` · Completed ${formatBackendDate(item.completed_at, { format: 'short' })}`}
                                  {item.expires_at && ` · Expires ${formatBackendDate(item.expires_at, { format: 'short' })}`}
                                </p>
                                {!quickOk && (
                                  <p className="text-xs text-amber-700 mt-0.5">
                                    ⚠ Needs correction before fast-approval
                                    {!item.mapped_training_code && ' — unmapped'}
                                    {!item.completed_at && ' — no completed date'}
                                    {!item.source_document_id && ' — no source certificate'}
                                  </p>
                                )}
                                {likelyDuplicateApproved && (
                                  <p className="text-xs text-amber-700 mt-0.5">
                                    Duplicate warning: this certificate already has an approved item mapped to the same qualification.
                                  </p>
                                )}
                              </div>

                              {/* Badges */}
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <Badge
                                  variant="outline"
                                  className={item.is_mandatory
                                    ? 'bg-red-50 text-red-700 border-red-200 text-xs'
                                    : 'text-gray-500 text-xs'}
                                >
                                  {item.is_mandatory ? 'Required' : 'Additional'}
                                </Badge>
                                {item.is_unmapped && (
                                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
                                    Unmapped
                                  </Badge>
                                )}
                              </div>

                              {/* Action buttons */}
                              {isAdmin && (
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  {quickOk ? (
                                    <>
                                      <Button
                                        size="sm"
                                        className="h-7 px-2 text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
                                        disabled={isVerifying || sourceErrors.proposedItems}
                                        onClick={() => openTrainingEvidenceReview(item)}
                                        data-testid="review-evidence-btn"
                                        title="Open the evidence to review before verifying"
                                      >
                                        <Eye className="h-3 w-3 mr-1" />
                                        Review Evidence
                                      </Button>
                                      {/* One action: Verify, Clarify and Reject all require opening source-linked evidence review. */}
                                    </>
                                  ) : (
                                    <>
                                      {item.mapped_training_code && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="h-7 px-2 text-xs border-blue-200 text-blue-700 hover:bg-blue-50"
                                          disabled={sourceErrors.proposedItems}
                                          onClick={() => handleEditProposed(item)}
                                          title="Add missing completion/expiry dates before review"
                                        >
                                          <Edit2 className="h-3 w-3 mr-1" />
                                          Add dates
                                        </Button>
                                      )}
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 px-2 text-xs"
                                        disabled={sourceErrors.proposedItems}
                                        onClick={() => {
                                          if (item.is_unmapped || !item.mapped_training_code) {
                                            const likelyMandatory = mandatoryTraining.some((m) => {
                                              const mk = normalizeTrainingAliasKey(m?.title || m?.code || m?.id);
                                              const ik = normalizeTrainingAliasKey(item?.raw_course_title || item?.mapped_training_title);
                                              return mk && ik && (mk === ik || mk.includes(ik) || ik.includes(mk));
                                            });
                                            if (likelyMandatory) {
                                              setOptionalApproveConfirmItem(item);
                                              return;
                                            }
                                          }
                                          openTrainingEvidenceReview(item);
                                        }}
                                      >
                                        <Eye className="h-3 w-3 mr-1" />
                                        {item.is_unmapped || !item.mapped_training_code
                                          ? 'Review optional mapping'
                                          : 'Review Evidence'}
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 px-2"
                                        disabled={sourceErrors.proposedItems}
                                        onClick={() => handleEditProposed(item)}
                                        title="Edit extracted values"
                                      >
                                        <Edit2 className="h-3 w-3" />
                                      </Button>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* All Training Records */}
              {sourceErrors.trainingRecords && (
                <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  <span className="font-medium">Cannot assess approved qualifications.</span> Canonical training records did not load, so the approved list is unavailable.
                </div>
              )}
              <div className="mb-3">
                <h4 className="text-sm font-medium text-gray-900">Approved qualifications · subject coverage</h4>
                <p className="text-sm text-gray-500">
                  Each row is one approved training record. A combined certificate can cover more than one subject; those subjects automatically count only toward role-required Mandatory Training and applicable induction checks when the evidence is verified and current.
                </p>
                {qualificationGroups.historical.length > 0 && (
                  <button type="button" className="mt-2 text-xs font-medium text-slate-700 underline"
                    aria-expanded={showHistoricalQualifications}
                    onClick={() => setShowHistoricalQualifications(v => !v)}>
                    {showHistoricalQualifications ? 'Hide' : 'Show'} expired optional / superseded evidence ({qualificationGroups.historical.length})
                  </button>
                )}
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[240px]">Training Name</TableHead>
                    <TableHead>Source Certificate</TableHead>
                    <TableHead>Completed</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead>Required?</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Verified By</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    const records = showHistoricalQualifications
                      ? [...qualificationGroups.current, ...qualificationGroups.historical]
                      : qualificationGroups.current;
                    return records
                      .filter(item => {
                        if (!searchQuery) return true;
                        const q = searchQuery.toLowerCase();
                        return item.title?.toLowerCase().includes(q) ||
                               item.code?.toLowerCase().includes(q);
                      })
                      .map((item) => (
                    <TableRow key={item.code || item.id}>
                      <TableCell>
                        <p className="font-medium text-gray-900">{item.title}</p>
                        {verifiedSafeguardingCoverage(item).length > 0 && (
                          <div className="mt-1 space-y-1" data-testid="qualification-safeguarding-coverage">
                            <p className="text-xs text-slate-500">
                              1 verified qualification · {verifiedSafeguardingCoverage(item).length} covered subject{verifiedSafeguardingCoverage(item).length === 1 ? '' : 's'}
                            </p>
                            {verifiedSafeguardingCoverage(item).map(subject => (
                              <div key={subject.code} className="flex items-center gap-1.5 text-xs text-emerald-800">
                                <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
                                <span>{subject.label}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {item.code === 'safeguarding' && item.verified && !verifiedSafeguardingCoverage(item).length && (
                          <p className="mt-1 text-xs text-amber-700">Subject coverage not confirmed · review original certificate</p>
                        )}
                        {item.provider && (
                          <p className="text-xs text-gray-500">{item.provider}</p>
                        )}
                        {item.history_count > 0 && (
                          <p className="text-xs text-indigo-500 mt-0.5">{item.history_count} record{item.history_count !== 1 ? 's' : ''} — history available</p>
                        )}
                      </TableCell>
                      <TableCell>
                        {(item.record_id || item.id) && (item.source_document_id || item.certificate_url || item.evidence_files?.length) ? (
                          <Button
                            variant="link"
                            size="sm"
                            className="h-auto p-0 text-blue-600"
                            onClick={() => onViewCertificate?.(item.record_id || item.id)}
                          >
                            <LinkIcon className="h-3 w-3 mr-1" />
                            Open certificate
                          </Button>
                        ) : item.certificate_url ? (
                          <a 
                            href={item.certificate_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-sm text-blue-600 hover:underline"
                          >
                            <LinkIcon className="h-3 w-3 mr-1" />
                            Open certificate
                          </a>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-gray-600">
                          {item.completed_at ? formatBackendDate(item.completed_at, { format: 'short' }) : '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        {item.expires_at ? (
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className={cn(
                              "text-sm",
                              getDisplayStatus(item) === 'expired' ? 'text-red-600 font-medium' :
                              getDisplayStatus(item) === 'expiring_soon' ? 'text-amber-600' :
                              'text-gray-600'
                            )}>
                              {formatBackendDate(item.expires_at, { format: 'short' })}
                            </span>
                            <DaysRemainingBadge expiresAt={item.expires_at} />
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">N/A</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {isRequiredTrainingItem(item) ? (
                          <Badge className="bg-red-100 text-red-700">Required</Badge>
                        ) : (
                          <Badge variant="outline" className="text-gray-500">Optional</Badge>
                        )}
                      </TableCell>
                      <TableCell>{renderStatusBadge(getDisplayStatus(item))}</TableCell>
                      <TableCell>
                        {item.verified_by ? (
                          <div className="text-xs">
                            <p className="text-gray-700">{item.verified_by}</p>
                            {item.verified_at && (
                              <p className="text-gray-400">
                                {formatBackendDate(item.verified_at, { format: 'short' })}
                              </p>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {/* Record details and history, separate from opening the certificate. */}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-9 gap-1 px-2"
                            onClick={() => handleOpenDetail(item)}
                            data-testid={`view-training-${item.code || item.id}`}
                            aria-label="Open training record details and history"
                            title="Open training record details and history"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                            <span>Details</span>
                          </Button>
                          
                          {/* Edit Button - Admin only */}
                          {isAdmin && item.completed_at && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-gray-500 hover:text-primary"
                              onClick={() => {
                                setEditingItem(item);
                                setEditDialogOpen(true);
                              }}
                              data-testid={`edit-training-${item.code || item.id}`}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                          )}
                          
                          {/* Verify/Unverify Button - Admin only */}
                          {isAdmin && item.completed_at && (
                            item.is_verified || item.status === 'verified' ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 px-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                onClick={() => handleUnverifyTraining(item)}
                                data-testid={`unverify-training-${item.code || item.id}`}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                <span className="text-xs">Unverify</span>
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 px-2 text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={() => openTrainingVerifyReview(item)}
                                data-testid={`verify-training-${item.code || item.id}`}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                <span className="text-xs">Verify</span>
                              </Button>
                            )
                          )}
                          
                          {/* Delete Button - Admin only */}
                          {isAdmin && (item.record_id || item.id) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => openDeleteDialog(item)}
                              data-testid={`delete-training-${item.code || item.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                  })()}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CERTIFICATES TAB */}
        <TabsContent value="certificates" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Award className="h-5 w-5 text-primary" />
                    Training Certificates
                  </CardTitle>
                  <CardDescription>
                    Certificates are the raw evidence library. Extraction creates review items; it does not verify training or satisfy Mandatory compliance by itself.
                  </CardDescription>
                </div>
                {isAdmin && (
                  <Button onClick={onUploadCertificate} data-testid="upload-cert-btn">
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Certificate
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {!sourceErrors.proposedItems && openTrainingItems.length > 0 && (
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-purple-200 bg-purple-50 p-3" data-testid="certificates-pending-review-banner">
                  <div>
                    <p className="text-sm font-semibold text-purple-900">{openTrainingItems.length} saved extracted course item(s) need review or follow-up</p>
                    <p className="text-xs text-purple-800">All Qualifications contains review items and follow-ups. Existing verified training stays separate from pending evidence.</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => { setReviewSourceDocumentId(null); setActiveTab('library'); }} data-testid="open-all-certificate-reviews">
                    <Eye className="h-4 w-4 mr-1" /> Review saved items
                  </Button>
                </div>
              )}
              {sourceErrors.proposedItems && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  Cannot load extracted training reviews. Please retry before re-extracting any saved certificate.
                </div>
              )}
              {sourceErrors.certificates ? (
                <div className="text-center py-12 text-red-700">
                  <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
                  <p className="font-medium">Cannot assess certificates</p>
                  <p className="mt-1 text-sm">Certificate evidence did not load. Certificate counts and extraction links are unavailable.</p>
                  <Button variant="outline" size="sm" onClick={fetchTrainingData} className="mt-4">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Retry
                  </Button>
                </div>
              ) : certificates.length === 0 ? (
                <div className="text-center py-12">
                  <Award className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No training certificates uploaded yet</p>
                  {isAdmin && (
                    <Button 
                      variant="outline" 
                      className="mt-4"
                      onClick={onUploadCertificate}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Upload First Certificate
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Separate: certificates, proposed_items, unmapped_items */}
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2">Uploaded Certificates</h4>
                    <ul className="space-y-2">
                      {groupedCertificates.map(cert => (
                        <li key={cert.id} className="border border-gray-200 rounded-lg p-3 flex items-center gap-3">
                          <FileText className="h-5 w-5 text-blue-600" />
                          <span className="font-medium flex-1 min-w-0 truncate">{cert.original_filename || cert.file_name || 'Training Certificate'}</span>
                          <span className="text-xs text-gray-500">Uploaded {formatBackendDate(cert.uploaded_at, { format: 'medium' })}</span>
                          {cert.pendingReviewCount > 0 && (
                            <Badge variant="outline" className="text-xs border-purple-300 text-purple-800">
                              {cert.pendingReviewCount} saved review item(s)
                            </Badge>
                          )}
                          {cert.followUpCount > 0 && (
                            <Badge variant="outline" className="text-xs border-amber-300 text-amber-900">
                              {cert.followUpCount} follow-up item(s)
                            </Badge>
                          )}
                          {isAdmin && !sourceErrors.proposedItems && (
                            (cert.pendingReviewCount > 0 || cert.followUpCount > 0) ? (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8"
                                onClick={() => { setReviewSourceDocumentId(cert.id); setActiveTab('library'); }}
                                data-testid={`review-saved-certificate-${cert.id}`}
                              >
                                <Eye className="h-3.5 w-3.5 mr-1" />
                                {cert.pendingReviewCount > 0 ? `Review ${cert.pendingReviewCount} saved item(s)` : `Inspect ${cert.followUpCount} follow-up(s)`}
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8"
                                onClick={() => setExtractExistingDocumentId(cert.id)}
                                data-testid={`review-existing-certificate-${cert.id}`}
                              >
                                <Wand2 className="h-3.5 w-3.5 mr-1" />
                                Extract &amp; Review
                              </Button>
                            )
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8"
                            onClick={() => setCertificatePreview({
                              id: cert.id,
                              filename: cert.original_filename || cert.file_name || 'Training Certificate',
                            })}
                            data-testid={`view-certificate-${cert.id}`}
                          >
                            <Eye className="h-3.5 w-3.5 mr-1" />
                            View
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </div>
                  {/* One review queue only: don't repeat extracted proposals as an unactionable list here. */}
                  {!sourceErrors.proposedItems && openTrainingItems.length === 0 && (
                    <p className="mt-4 text-sm text-slate-600">No outstanding extracted training reviews or follow-ups. Certificates remain available as evidence.</p>
                  )}
                  {Array.isArray(unmappedItems) && unmappedItems.length > 0 && (
                    <div className="mt-6">
                      <h4 className="font-semibold text-amber-900 mb-2">Unmapped Items</h4>
                      <ul className="space-y-2">
                        {unmappedItems.map((item) => (
                          <li
                            key={item.id || item.raw_course_title}
                            className="border border-amber-200 rounded-lg p-3 flex items-center gap-3"
                          >
                            <AlertTriangle className="h-4 w-4 text-amber-600" />
                            <span className="font-medium">
                              {item.raw_course_title || item.title || 'Unmapped training item'}
                            </span>
                            <span className="text-xs text-amber-700">Unmapped</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="service_readiness" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Shield className="h-5 w-5 text-primary" />
                    Service & Tender Capability Readiness
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Uses the same verified training and competency evidence as CareOps. These profiles are advisory only and do not change onboarding or work-readiness gates.
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openWorkforceTrainingMatrix('all')}
                  data-testid="open-workforce-training-matrix"
                >
                  <LinkIcon className="mr-2 h-4 w-4" />
                  Workforce matrix
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                <p className="font-medium">Safe rollout mode</p>
                <p className="mt-1 text-blue-800">
                  Assigning a service profile helps identify evidence gaps for a specialist service or tender. It does not make an existing worker non-compliant.
                </p>
              </div>

              {sourceErrors.capabilityProfiles ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                  Service readiness could not be assessed right now. Mandatory training remains unaffected.
                </div>
              ) : !capabilityReadiness ? (
                <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading service capability profiles…
                </div>
              ) : (
                <div className="space-y-4">
                  {(capabilityReadiness.available_profiles || []).map((profile) => {
                    const assignedProfile = (capabilityReadiness.assigned_profiles || [])
                      .find((item) => item.profile_id === profile.id);
                    const assigned = Boolean(assignedProfile);
                    const capabilitySummary = assignedProfile?.summary || {};
                    return (
                      <div
                        key={profile.id}
                        className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
                        data-testid={`capability-profile-${profile.id}`}
                      >
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <h4 className="font-semibold text-slate-900">{profile.label}</h4>
                              <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">
                                Advisory
                              </Badge>
                              {assigned && (
                                <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
                                  Assigned
                                </Badge>
                              )}
                            </div>
                            <p className="mt-1 max-w-3xl text-sm text-slate-600">{profile.description}</p>
                            {profile.framework_reference && (
                              <p className="mt-1 text-xs text-slate-500">
                                Framework reference: {profile.framework_reference}
                              </p>
                            )}
                          </div>
                          {isAdmin && (
                            <Button
                              variant={assigned ? 'outline' : 'default'}
                              size="sm"
                              onClick={() => handleCapabilityProfileToggle(profile.id, assigned)}
                              disabled={capabilityMutating === profile.id}
                              data-testid={`toggle-capability-profile-${profile.id}`}
                            >
                              {capabilityMutating === profile.id && (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              )}
                              {assigned ? 'Remove profile' : 'Enable profile'}
                            </Button>
                          )}
                        </div>

                        {!assigned ? (
                          <div className="mt-4 grid gap-3 sm:grid-cols-3">
                            <div className="rounded-lg bg-slate-50 p-3">
                              <p className="text-xs text-slate-500">Core capabilities</p>
                              <p className="mt-1 text-lg font-semibold text-slate-900">{profile.essential_count}</p>
                            </div>
                            <div className="rounded-lg bg-slate-50 p-3">
                              <p className="text-xs text-slate-500">Conditional capabilities</p>
                              <p className="mt-1 text-lg font-semibold text-slate-900">{profile.conditional_count}</p>
                            </div>
                            <div className="rounded-lg bg-slate-50 p-3">
                              <p className="text-xs text-slate-500">Effect on current onboarding</p>
                              <p className="mt-1 text-sm font-semibold text-emerald-700">None</p>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-4 space-y-4">
                            <div className="grid gap-3 sm:grid-cols-3">
                              <div className="rounded-lg bg-slate-50 p-3">
                                <p className="text-xs text-slate-500">Core evidence ready</p>
                                <p className="mt-1 text-lg font-semibold text-slate-900">
                                  {capabilitySummary.essential_satisfied || 0}/{capabilitySummary.essential_total || 0}
                                </p>
                              </div>
                              <div className="rounded-lg bg-slate-50 p-3">
                                <p className="text-xs text-slate-500">Advisory readiness</p>
                                <p className="mt-1 text-lg font-semibold text-slate-900">
                                  {capabilitySummary.essential_percentage ?? 0}%
                                </p>
                              </div>
                              <div className="rounded-lg bg-slate-50 p-3">
                                <p className="text-xs text-slate-500">Needs attention</p>
                                <p className="mt-1 text-lg font-semibold text-slate-900">
                                  {capabilitySummary.attention_total || 0}
                                </p>
                              </div>
                            </div>

                            <Progress value={capabilitySummary.essential_percentage || 0} className="h-2" />

                            <div className="overflow-hidden rounded-lg border border-slate-200">
                              {(assignedProfile.requirements || []).map((requirement) => (
                                <div
                                  key={requirement.capability_code}
                                  className="flex flex-col gap-2 border-b border-slate-100 px-4 py-3 last:border-b-0 sm:flex-row sm:items-center sm:justify-between"
                                >
                                  <div className="min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <p className="font-medium text-slate-900">{requirement.label}</p>
                                      <Badge variant="outline" className="text-[11px]">
                                        {requirement.conditional ? 'Conditional' : 'Core'}
                                      </Badge>
                                    </div>
                                    <p className="mt-0.5 text-xs text-slate-500">{requirement.reason}</p>
                                    {requirement.evidence?.title && (
                                      <p className="mt-1 text-xs text-slate-600">
                                        Evidence: {requirement.evidence.title}
                                      </p>
                                    )}
                                  </div>
                                  <div className="flex shrink-0 items-center gap-2">
                                    {renderStatusBadge(requirement.status)}
                                    {requirement.deployment_blocking === false && (
                                      <span className="text-[11px] text-slate-400">advisory</span>
                                    )}
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 px-2 text-xs"
                                      onClick={() => openWorkforceTrainingMatrix(
                                        ['expired', 'overdue'].includes(requirement.status)
                                          ? 'expired'
                                          : ['due_soon', 'expiring_soon'].includes(requirement.status)
                                            ? 'expiring_soon'
                                            : ['awaiting_review', 'proposed', 'needs_information'].includes(requirement.status)
                                              ? 'awaiting_review'
                                              : requirement.status === 'missing'
                                                ? 'missing'
                                                : 'all'
                                      )}
                                    >
                                      Matrix
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="h-5 w-5 text-blue-600" />
              Edit Training Record
            </DialogTitle>
            <DialogDescription>
              Correct the details for {editingItem?.title || editingItem?.training_title || 'this training'}
            </DialogDescription>
          </DialogHeader>
          
          {editingItem && (
            <div className="space-y-4 py-4">
              {/* Completion Date */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Completion Date</label>
                <Input
                  type="date"
                  defaultValue={editingItem.completed_at?.split('T')[0] || ''}
                  onChange={(e) => setEditingItem({...editingItem, completed_at: e.target.value})}
                  data-testid="edit-training-completion-date"
                />
              </div>
              
              {/* Expiry Date */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Expiry Date</label>
                <Input
                  type="date"
                  value={noExpiryChecked ? '' : (editingItem.expires_at?.split('T')[0] || editingItem.expiry_date?.split('T')[0] || '')}
                  onChange={(e) => {
                    setNoExpiryChecked(false);
                    setEditingItem({...editingItem, expires_at: e.target.value, expiry_date: e.target.value});
                  }}
                  disabled={noExpiryChecked}
                  data-testid="edit-training-expiry-date"
                />
                <p className="text-xs text-slate-500">Leave blank for training that doesn't expire</p>
                <label className="flex items-center gap-2 text-xs text-slate-600">
                  <input
                    type="checkbox"
                    checked={noExpiryChecked}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setNoExpiryChecked(checked);
                      if (checked) {
                        setEditingItem({ ...editingItem, expires_at: null, expiry_date: null });
                      }
                    }}
                  />
                  This training does not expire
                </label>
              </div>
              
              {/* Provider */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Provider</label>
                <Input
                  type="text"
                  defaultValue={editingItem.provider || editingItem.training_provider || ''}
                  onChange={(e) => setEditingItem({...editingItem, provider: e.target.value})}
                  placeholder="e.g., Skills for Care, Company Training"
                  data-testid="edit-training-provider"
                />
              </div>
              
              {/* Certificate Number */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Certificate Number (optional)</label>
                <Input
                  type="text"
                  defaultValue={editingItem.certificate_number || ''}
                  onChange={(e) => setEditingItem({...editingItem, certificate_number: e.target.value})}
                  placeholder="e.g., CERT-12345"
                  data-testid="edit-training-cert-number"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setEditDialogOpen(false);
                setEditingItem(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!editingItem) return;
                try {
                  // Use the PATCH endpoint which accepts bulk updates with proper format
                  const trainingCode =
                    editingItem.code ||
                    editingItem.requirement_id ||
                    editingItem.training_code ||
                    editingItem.mapped_training_code;
                  if (!trainingCode) {
                    toast.error('Training mapping is missing. Use Review Evidence to map this item before editing dates.');
                    return;
                  }
                  
                  await axios.patch(
                    `${API}/employees/${employeeId}/training/${trainingCode}`,
                    {
                      completion_date: editingItem.completed_at,
                      expiry_date: editingItem.expires_at || editingItem.expiry_date || null,
                      reason: 'Admin date correction via Edit dialog'
                    },
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                  toast.success('Training record updated successfully');
                  setEditDialogOpen(false);
                  setEditingItem(null);
                  revalidateTrainingData();
                } catch (err) {
                  console.error('Error updating training:', err);
                  toast.error(err.response?.data?.detail || 'Failed to update training record');
                }
              }}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="edit-training-save-btn"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* One click opens the actual document securely — no Details > Evidence > View. */}
      <InlineDocumentViewer
        open={Boolean(certificatePreview)}
        onClose={() => setCertificatePreview(null)}
        fetchUrl={certificatePreview?.id
          ? `${API}/employee-documents/${encodeURIComponent(certificatePreview.id)}/file`
          : null}
        title={certificatePreview?.filename || 'Training Certificate'}
        filename={certificatePreview?.filename || 'Training Certificate'}
        token={token}
      />

      {/* Training Detail Drawer */}
      <TrainingDetailDrawer
        isOpen={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedTraining(null);
        }}
        trainingItem={selectedTraining}
        employeeId={employeeId}
        onUpdate={() => {
          revalidateTrainingData();
        }}
        isAdmin={isAdmin}
      />

      <TrainingRequestDialog
        employeeId={employeeId}
        employeeName={employeeName}
        open={requestDialogOpen}
        initialSelectedTypes={requestDialogDefaults.trainingTypes}
        initialIncludeRenewals={requestDialogDefaults.includeRenewals}
        onClose={() => setRequestDialogOpen(false)}
        onComplete={revalidateTrainingData}
      />

      {/* Use the already-stored evidence, never ask the administrator to upload it again. */}
      <TrainingIntakeWizard
        employeeId={employeeId}
        employeeName={employeeName}
        open={Boolean(extractExistingDocumentId)}
        existingDocumentId={extractExistingDocumentId}
        onClose={() => setExtractExistingDocumentId(null)}
        onComplete={() => {
          setExtractExistingDocumentId(null);
          revalidateTrainingData();
        }}
      />

      {/* AI Certificate Extractor with Preview Step */}
      <TrainingCertificateExtractor
        employeeId={employeeId}
        employeeName={employeeName}
        isOpen={extractorOpen}
        onClose={() => setExtractorOpen(false)}
        onSuccess={() => {
          revalidateTrainingData();
        }}
      />

      <EvidenceReviewViewerDialog
        isOpen={trainingReviewOpen}
        onClose={closeTrainingReview}
        file={trainingReviewPurpose === 'verify'
          ? getSourceCertificateFileForTrainingItem(trainingReviewItem)
          : getSourceCertificateFileForProposedItem(trainingReviewItem)}
        employeeId={employeeId}
        employeeName={employeeName}
        requirementType="training_certificate"
        mode="training-review"
        trainingItem={trainingReviewItem}
        trainingMappingOptions={trainingMappingOptions}
        trainingAcceptLabel={trainingReviewPurpose === 'verify' ? 'Verify training' : 'Verify after review'}
        trainingRejectLabel={trainingReviewPurpose === 'verify' ? null : 'Reject / needs correction'}
        trainingCompletionMessage={trainingReviewPurpose === 'verify'
          ? 'Training verification has been recorded after evidence review.'
          : 'The extracted training item decision has been recorded. Verification remains a separate step on the canonical training record.'}
        onTrainingAccepted={async (payload) => {
          if (!trainingReviewItem) return;
          if (trainingReviewPurpose === 'verify') {
            await submitVerifyTraining(trainingReviewItem);
          } else {
            await handleApproveProposed(trainingReviewItem, payload?.notes, payload || {});
          }
        }}
        onTrainingRejected={trainingReviewPurpose === 'verify' ? undefined : async ({ notes }) => {
          if (!trainingReviewItem) return;
          await handleRejectProposed(trainingReviewItem, notes);
        }}
      />
      
      {/* Delete Training Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete Training Record
            </DialogTitle>
            <DialogDescription>
              This will soft-delete "{deletingItem?.title}" from the employee's training records.
              The record will be archived for audit purposes.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-red-50 rounded-lg border border-red-100">
              <p className="text-sm text-red-800">
                <strong>Training:</strong> {deletingItem?.title}
              </p>
              {deletingItem?.completed_at && (
                <p className="text-sm text-red-600 mt-1">
                  Completed: {formatBackendDate(deletingItem.completed_at, { format: 'short' })}
                </p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">
                Reason for deletion (required for audit trail)
              </label>
              <Input
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="e.g., Test data, duplicate entry, uploaded in error..."
                data-testid="delete-reason-input"
              />
            </div>
            {deletingItem?.source_document_id && (
              <label className="flex items-start gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={deleteLinkedRecords}
                  onChange={(e) => setDeleteLinkedRecords(e.target.checked)}
                />
                <span>
                  Remove all approved qualifications linked to the same source certificate.
                </span>
              </label>
            )}
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setDeletingItem(null);
                setDeleteReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteTraining}
              disabled={deleting || !deleteReason.trim()}
              data-testid="confirm-delete-btn"
            >
              {deleting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Delete Training
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={unverifyDialogOpen} onOpenChange={setUnverifyDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Unverify Training Record
            </DialogTitle>
            <DialogDescription>
              This will mark "{unverifyItem?.title}" as unverified and require re-verification.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <label className="block text-sm font-medium">Reason *</label>
            <Input
              value={unverifyReason}
              onChange={(e) => setUnverifyReason(e.target.value)}
              placeholder="Reason for unverifying this training record"
              data-testid="unverify-reason-input"
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setUnverifyDialogOpen(false);
                setUnverifyItem(null);
                setUnverifyReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={confirmUnverifyTraining}
              disabled={unverifying || !unverifyReason.trim()}
              data-testid="confirm-unverify-btn"
            >
              {unverifying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Unverify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove certificate confirm dialog */}
      <Dialog open={!!removeCertDialogCert} onOpenChange={open => { if (!open) setRemoveCertDialogCert(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove certificate?</DialogTitle>
            <DialogDescription>
              This will delete the certificate file and any extracted training items that have not yet been approved.
              Already-approved training records will be preserved.
            </DialogDescription>
          </DialogHeader>
          {removeCertDialogCert && (
            <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 mt-2">
              <p className="font-medium">{removeCertDialogCert.original_filename || removeCertDialogCert.file_name || 'Training Certificate'}</p>
              <p className="text-xs text-slate-500 mt-0.5">Uploaded {formatBackendDate(removeCertDialogCert.uploaded_at, { format: 'medium' })}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveCertDialogCert(null)} disabled={removingCert}>
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleRemoveCertificate}
              disabled={removingCert}
              data-testid="confirm-remove-cert-btn"
            >
              {removingCert ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Remove certificate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!optionalApproveConfirmItem} onOpenChange={(open) => { if (!open) setOptionalApproveConfirmItem(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm optional qualification route</DialogTitle>
            <DialogDescription>
              This extracted item looks similar to a mandatory training requirement. Approving as optional may leave a work blocker unresolved.
            </DialogDescription>
          </DialogHeader>
          {optionalApproveConfirmItem && (
            <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              {optionalApproveConfirmItem.raw_course_title || optionalApproveConfirmItem.mapped_training_title}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOptionalApproveConfirmItem(null)}>Cancel</Button>
            <Button
              onClick={() => {
                if (optionalApproveConfirmItem) openTrainingEvidenceReview(optionalApproveConfirmItem);
                setOptionalApproveConfirmItem(null);
              }}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              Continue to review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

